// 实时画面：动态加载 noVNC（/novnc/core/rfb.js，ES module），连 <ws origin>/vnc?token=&task=&mode=
// 默认只看（viewOnly）；只有接管状态下才用 control 模式
import { useEffect, useRef, useState } from "react";
import { getToken } from "../lib/api";
import { LiveStatus } from "./LiveStatus";
import { BrowserLive } from "./BrowserLive";
import { apiUrl, wsUrl } from "../lib/net";

interface RfbLike {
  viewOnly: boolean;
  scaleViewport: boolean;
  resizeSession: boolean;
  background: string;
  disconnect(): void;
  addEventListener(ev: string, cb: (e: { detail?: { clean?: boolean; reason?: string } }) => void): void;
}
type RfbCtor = new (target: HTMLElement, url: string, opts?: Record<string, unknown>) => RfbLike;

let rfbModule: Promise<RfbCtor> | null = null;
function loadRfb(): Promise<RfbCtor> {
  if (!rfbModule) {
    const url = `${window.location.origin}/novnc/core/rfb.js`;
    rfbModule = import(/* @vite-ignore */ url).then((m: { default: RfbCtor }) => m.default);
    rfbModule.catch(() => {
      rfbModule = null;
    });
  }
  return rfbModule;
}

function LegacyVncViewer({ taskId, mode, height }: { taskId: string; mode: "view" | "control"; height?: number }) {
  const box = useRef<HTMLDivElement>(null);
  const [state, setState] = useState<"loading" | "connected" | "closed" | "error">("loading");
  const [msg, setMsg] = useState<string>("正在连接实时画面…");
  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    let rfb: RfbLike | null = null;
    let cancelled = false;
    setState("loading");
    setMsg("正在连接实时画面…");
    const token = getToken() ?? "";
    loadRfb()
      .then((RFB) => {
        if (cancelled || !box.current) return;
        const url = wsUrl(`/vnc?token=${encodeURIComponent(token)}&task=${encodeURIComponent(taskId)}&mode=${mode}`);
        rfb = new RFB(box.current, url, { shared: true });
        rfb.viewOnly = mode !== "control";
        rfb.scaleViewport = true;
        rfb.resizeSession = false;
        rfb.background = "#000";
        rfb.addEventListener("connect", () => { if (!cancelled) setState("connected"); });
        rfb.addEventListener("disconnect", (e) => {
          if (cancelled) return;
          setState("closed");
          setMsg(e.detail?.clean ? "实时画面已断开" : "实时画面连接失败，可点「重新连接」");
        });
      })
      .catch(() => {
        if (cancelled) return;
        setState("error");
        setMsg("实时画面组件加载失败（noVNC 不可用）");
      });
    return () => {
      cancelled = true;
      try {
        rfb?.disconnect();
      } catch {
        /* 已断开 */
      }
    };
  }, [taskId, mode, nonce]);

  return (
    <div className="live-viewer">
      <div className="row small live-toolbar" style={{ marginBottom: 6 }}>
        {mode === "control" ? (
          <b className="err">● 可操作模式：你的鼠标键盘会直接操作执行机浏览器</b>
        ) : (
          <span className="muted">只看模式（不能点）</span>
        )}
        <span className="spacer" />
        <LiveStatus state={state} />
        {(state === "closed" || state === "error") && (
          <button className="btn btn-small" onClick={() => setNonce((n) => n + 1)}>
            重新连接
          </button>
        )}
      </div>
      <div className={`vnc live-stage${mode === "control" ? " control" : ""}`} style={height ? { height } : undefined} data-testid="vnc">
        <div ref={box} style={{ width: "100%", height: "100%" }} />
        {state !== "connected" && <div className="vnc-msg">{msg}</div>}
      </div>
    </div>
  );
}

export function VncViewer(props: { taskId: string; mode: "view" | "control"; height?: number }) {
  const [kind,setKind]=useState<"browser"|"vnc"|null>(null);
  const [error,setError]=useState("");
  const [nonce,setNonce]=useState(0);
  useEffect(()=>{let cancelled=false;setError("");setKind(null);fetch(apiUrl("/api/ping")).then(r=>{if(!r.ok)throw new Error("ping failed");return r.json();}).then(p=>{if(!cancelled)setKind(p.liveView==="browser"?"browser":"vnc");}).catch(()=>{if(!cancelled)setError("无法确认实时画面连接，请刷新重试");});return()=>{cancelled=true;};},[nonce]);
  if(error)return <div className="live-viewer"><div className="row live-toolbar"><LiveStatus state="error"/><button className="btn btn-small" onClick={()=>setNonce(n=>n+1)}>重新连接</button></div><p className="notice warn">{error}</p></div>;
  if(kind===null)return <div className="live-viewer"><LiveStatus state="loading"/><p>正在连接实时画面…</p></div>;
  return kind==="browser"?<BrowserLive {...props}/>:<LegacyVncViewer {...props}/>;
}
